// src/shared/hooks/useSupportTickets.ts

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '@/shared/services/apiClient';
import socketService from '@/shared/services/socket';

export interface SupportTicket {
  id: string;
  ticket_id: string;
  user_id: string;
  category: string;
  subject: string;
  message: string;
  user_email: string | null;
  user_phone: string | null;
  user_company: string | null;
  user_plan: string | null;
  user_location: string | null;
  status: 'open' | 'pending' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  created_at: string;
  updated_at: string;
}

export interface SupportMessage {
  id: string;
  user_id: string;
  ticket_id: string;
  subject: string | null;
  content: string;
  image_url: string | null;
  from_admin: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Hook to fetch and manage support tickets
 */
export function useSupportTickets() {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get<{
        ok: boolean;
        tickets: SupportTicket[];
        error?: string;
      }>('/support/tickets');

      if (!response.ok) {
        throw new Error(response.error || 'Failed to fetch tickets');
      }

      setTickets(response.tickets || []);
    } catch (err) {
      console.error('[useSupportTickets] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();

    // Listen for ticket updates via socket
    socketService.on('support:ticket:new', (ticket: SupportTicket) => {
      setTickets(prev => [ticket, ...prev]);
    });

    socketService.on('support:ticket:update', (data: { ticketId: string; status?: string }) => {
      setTickets(prev => prev.map(t =>
        t.ticket_id === data.ticketId ? { ...t, ...data } : t
      ));
    });

    return () => {
      socketService.off('support:ticket:new');
      socketService.off('support:ticket:update');
    };
  }, [fetchTickets]);

  return {
    tickets,
    loading,
    error,
    refetch: fetchTickets,
  };
}

/**
 * Hook to manage a specific ticket's chat
 */
export function useTicketChat(ticketId: string | null) {
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [isAdminTyping, setIsAdminTyping] = useState(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchMessages = useCallback(async () => {
    if (!ticketId) return;

    try {
      setLoading(true);

      const response = await apiClient.get<{
        ok: boolean;
        ticket: {
          ticket: SupportTicket;
          messages: SupportMessage[];
        };
        error?: string;
      }>(`/support/tickets/${ticketId}`);

      if (!response.ok) {
        throw new Error(response.error || 'Failed to fetch messages');
      }

      setMessages(response.ticket.messages || []);
    } catch (err) {
      console.error('[useTicketChat] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (!ticketId) {
      setMessages([]);
      return;
    }

    fetchMessages();

    // Join ticket room for real-time updates
    socketService.emit('support:ticket:join', { ticketId });

    // Listen for new messages
    const handleNewMessage = (message: SupportMessage) => {
      if (message.ticket_id === ticketId) {
        setMessages(prev => {
          // Avoid duplicates
          if (prev.some(m => m.id === message.id)) return prev;
          return [...prev, message];
        });
      }
    };

    // Listen for typing indicators
    const handleTyping = (data: { ticketId: string; isTyping: boolean; isAdmin: boolean }) => {
      if (data.ticketId === ticketId && data.isAdmin) {
        setIsAdminTyping(data.isTyping);

        // Auto-clear typing after 3 seconds
        if (data.isTyping) {
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }
          typingTimeoutRef.current = setTimeout(() => {
            setIsAdminTyping(false);
          }, 3000);
        }
      }
    };

    socketService.on('support:message:new', handleNewMessage);
    socketService.on('support:typing', handleTyping);

    return () => {
      socketService.off('support:message:new', handleNewMessage);
      socketService.off('support:typing', handleTyping);
      socketService.emit('support:ticket:leave', { ticketId });
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [ticketId, fetchMessages]);

  const sendMessage = useCallback(async (content: string, imageUrl?: string) => {
    if (!ticketId || (!content.trim() && !imageUrl)) return null;

    try {
      setSending(true);

      const response = await apiClient.post<{
        ok: boolean;
        message: SupportMessage;
        error?: string;
      }>(`/support/tickets/${ticketId}/messages`, {
        content: content.trim() || ' ',
        imageUrl,
      });

      if (!response.ok) {
        throw new Error(response.error || 'Failed to send message');
      }

      // Add message to local state immediately for instant UI update
      setMessages(prev => {
        // Avoid duplicates
        if (prev.some(m => m.id === response.message.id)) return prev;
        return [...prev, response.message];
      });

      // Emit via socket for real-time delivery to other clients
      // Pass the saved message so the backend just broadcasts it without re-saving
      socketService.emit('support:message:send', {
        ticketId,
        content: content.trim(),
        imageUrl,
        fromAdmin: false,
        message: response.message,
      });

      return response.message;
    } catch (err) {
      console.error('[useTicketChat] Error sending message:', err);
      throw err;
    } finally {
      setSending(false);
    }
  }, [ticketId]);

  const sendTypingIndicator = useCallback((isTyping: boolean) => {
    if (!ticketId) return;
    socketService.emit('support:typing', { ticketId, isTyping, isAdmin: false });
  }, [ticketId]);

  return {
    messages,
    loading,
    sending,
    isAdminTyping,
    sendMessage,
    sendTypingIndicator,
    refetch: fetchMessages,
  };
}
